import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ProjectMember } from '../types';
import { listProjectMembers, addProjectMember, removeProjectMember, getProject } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { useToast } from '../components/Toast';
import { useTranslation } from 'react-i18next';

const ProjectMembers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const { t } = useTranslation('profile');
  const [projectName, setProjectName] = useState('');
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ userId: string; username: string } | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      getProject(id).then(p => setProjectName(p.name)),
      listProjectMembers(id).then(setMembers),
    ]).finally(() => setLoading(false));
  }, [id]);

  const handleAdd = async () => {
    if (!id || !input.trim()) return;
    setAdding(true);
    try {
      await addProjectMember(id, input.trim());
      const updated = await listProjectMembers(id);
      setMembers(updated);
      setInput('');
      toast.success(t('members.addSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('members.addFailed'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async () => {
    if (!id || !confirmRemove) return;
    setRemoving(true);
    try {
      await removeProjectMember(id, confirmRemove.userId);
      setMembers(prev => prev.filter(m => m.userId !== confirmRemove.userId));
      toast.success(t('members.removeSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('members.removeFailed'));
    } finally {
      setRemoving(false);
      setConfirmRemove(null);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('members.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <Breadcrumb items={[
        { label: t('members.breadcrumbProjects'), to: '/projects' },
        { label: projectName || t('members.breadcrumbProject'), to: `/projects/${id}` },
        { label: t('members.breadcrumbTitle') },
      ]} />

      <div className="page-header">
        <h1 className="page-title">{t('members.pageTitle')}</h1>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1.5rem' }}>
          <input
            className="input"
            style={{ flex: 1 }}
            placeholder={t("members.placeholder")}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            disabled={adding}
          />
          <button className="btn btn-primary" onClick={handleAdd} disabled={adding || !input.trim()} style={{ flexShrink: 0 }}>
            {adding ? t('members.addBtnAdding') : t('members.addBtn')}
          </button>
        </div>

        {members.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {members.map(m => (
              <div key={m.id} className="list-item">
                <div>
                  <span style={{ fontWeight: 500 }}>{m.username}</span>
                </div>
                <div className="list-item-actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--danger)' }}
                    onClick={() => setConfirmRemove({ userId: m.userId, username: m.username })}
                  >
                    {t('members.removeBtn')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon="&#128101;"
            description={t("members.emptyMembers")}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!confirmRemove}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={handleRemove}
        title={t("members.removeTitle")}
        message={t("members.removeConfirm", { name: confirmRemove?.username })}
        confirmText={t("members.removeConfirmBtn")}
        confirmDanger
        loading={removing}
      />
    </div>
  );
};

export default ProjectMembers;
